import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, Button, Box, IconButton, Drawer, List, ListItem, ListItemButton, ListItemText, Menu, MenuItem, Select, FormControl } from '@mui/material';
import { useHistory } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LanguageIcon from '@mui/icons-material/Language';
import { apiService } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const Layout = ({ children }) => {
  const history = useHistory();
  const { language, changeLanguage, t } = useLanguage();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(!!apiService.getAuthToken());

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleLogout = () => {
    apiService.logout();
    setIsAuthenticated(false);
    handleUserMenuClose();
    history.push('/');
  };

  const handleNavigation = (path) => {
    history.push(path);
    setMobileOpen(false);
  };

  const navigationItems = [
    { label: t.home, path: '/' },
    { label: t.properties, path: '/all-properties' },
    { label: t.services, path: '/all-services' },
  ];

  const drawerContent = (
    <Box sx={{ width: 250 }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            component="img"
            src="/images/logo.jpg"
            alt="Bisauli Logo"
            sx={{
              height: 40,
              width: 'auto',
              objectFit: 'contain',
              borderRadius: '4px'
            }}
          />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea' }}>
            अपना बिसौली
          </Typography>
        </Box>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {navigationItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton 
              onClick={() => handleNavigation(item.path)}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(102, 126, 234, 0.1)',
                  color: '#667eea'
                }
              }}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding>
          <ListItemButton
            sx={{
              backgroundColor: 'rgba(102, 126, 234, 0.1)',
              '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.2)' }
            }}
          >
            <FormControl fullWidth size="small">
              <Select
                value={language}
                onChange={(e) => changeLanguage(e.target.value)}
                sx={{
                  color: '#667eea',
                  fontWeight: 600,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea'
                  }
                }}
              >
                <MenuItem value="hindi">हिंदी (Hindi)</MenuItem>
                <MenuItem value="english">English</MenuItem>
              </Select>
            </FormControl>
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="sticky" 
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 2px 12px rgba(102, 126, 234, 0.15)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <Toolbar 
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: { xs: 1, sm: 1.5 },
            px: { xs: 1.5, sm: 2, md: 3 }
          }}
        >
          {/* Logo Section - Desktop & Mobile */}
          <Box
            onClick={() => handleNavigation('/')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1.5 },
              cursor: 'pointer',
              transition: 'transform 0.3s',
              '&:hover': { transform: 'scale(1.05)' }
            }}
          >
            <Box
              component="img"
              src="/images/logo.jpg"
              alt="Bisauli Logo"
              sx={{
                height: { xs: 35, sm: 45, md: 50 },
                width: 'auto',
                objectFit: 'contain',
                borderRadius: '4px'
              }}
            />
            {/* App Name - Hidden on Extra Small Screens */}
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <Typography 
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: 'white',
                  fontSize: { sm: '1rem', md: '1.2rem' },
                  letterSpacing: '-0.5px'
                }}
              >
                अपना बिसौली
              </Typography>
              <Typography 
                variant="caption"
                sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: { sm: '0.65rem', md: '0.75rem' },
                  fontWeight: 500
                }}
              >
                Properties & Services
              </Typography>
            </Box>
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 3, alignItems: 'center' }}>
            {navigationItems.map((item) => (
              <Button
                key={item.path}
                color="inherit"
                onClick={() => handleNavigation(item.path)}
                sx={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -4,
                    left: 0,
                    width: 0,
                    height: 2,
                    backgroundColor: 'white',
                    transition: 'width 0.3s'
                  },
                  '&:hover::after': {
                    width: '100%'
                  }
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>

          {/* Right Section - Language and Auth */}
          <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1, md: 2 }, alignItems: 'center' }}>
            {/* Language Selector */}
            <FormControl 
              variant="outlined" 
              size="small"
              sx={{
                minWidth: { xs: 100, sm: 140 },
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: 1,
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)'
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.8)'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'white'
                  }
                },
                '& .MuiSvgIcon-root': {
                  color: 'white !important'
                }
              }}
            >
              <Select
                value={language}
                onChange={(e) => changeLanguage(e.target.value)}
                startAdornment={
                  <Box sx={{ mr: 0.5, display: 'flex', alignItems: 'center' }}>
                    <LanguageIcon sx={{ fontSize: '1.2rem', color: 'white' }} />
                  </Box>
                }
              >
                <MenuItem value="hindi">हिंदी</MenuItem>
                <MenuItem value="english">English</MenuItem>
              </Select>
            </FormControl>

            {/* Authentication Buttons */}
            {isAuthenticated ? (
              <>
                <Button
                  color="inherit"
                  onClick={handleUserMenuOpen}
                  sx={{
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    display: { xs: 'none', sm: 'block' }
                  }}
                >
                  👤
                </Button>
                <Menu
                  anchorEl={userMenuAnchorEl}
                  open={Boolean(userMenuAnchorEl)}
                  onClose={handleUserMenuClose}
                >
                  <MenuItem onClick={() => { handleNavigation('/admin-dashboard'); handleUserMenuClose(); }}>
                    {t.adminDashboard}
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    {t.logout}
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button
                color="inherit"
                onClick={() => handleNavigation('/login')}
                sx={{
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  fontWeight: 600,
                  border: '2px solid white',
                  borderRadius: 1,
                  px: { xs: 1.5, sm: 2 },
                  py: 0.5,
                  transition: 'all 0.3s',
                  '&:hover': {
                    backgroundColor: 'white',
                    color: '#667eea'
                  }
                }}
              >
                {t.login}
              </Button>
            )
            }

            {/* Mobile Menu Icon */}
            <IconButton
              color="inherit"
              aria-label="menu"
              onClick={handleDrawerToggle}
              sx={{ display: { xs: 'flex', md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          '& .MuiDrawer-paper': {
            width: 250,
            backgroundColor: '#f5f5f5'
          }
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box>
        {children}
      </Box>
    </>
  );
};

export default Layout;